import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import client from '@/api/client';
import { useAuth } from '@/context/AuthContext';

/**
 * FlagsContext (r7.5) — feature flags + A/B variants evaluated by the edge (GET /api/flags).
 *
 *   const { enabled, variant } = useFlag('resale_v2');
 *   const flags = useFlags();                 // { ready, get(key), refresh() }
 *
 * Fetched once at boot and again on login/logout (the subject changes: device → user). The server is the
 * single evaluator — the web never computes rollouts itself, so an A/B bucket seen here is the bucket every
 * service sees for the same user. A 304 (ETag) keeps the previous map.
 */
const FlagsContext = createContext(null);
const REFRESH_MS = Number(import.meta.env.VITE_FLAGS_REFRESH_MS || 5 * 60 * 1000);

export function FlagsProvider({ children }) {
  const { user, loading } = useAuth();
  const [flags, setFlags] = useState({});
  const [ready, setReady] = useState(false);
  const etag = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await client.get('/flags', { headers: etag.current ? { 'If-None-Match': etag.current } : {}, validateStatus: (s) => s === 200 || s === 304 });
      if (res.status === 200 && res.data && res.data.flags) {
        setFlags(res.data.flags);
        etag.current = res.headers?.etag || null;
      }
    } catch { /* keep whatever we had; flags are additive, never blocking */ }
    setReady(true);
  }, []);

  useEffect(() => { if (!loading) { etag.current = null; refresh(); } }, [loading, user?.id, refresh]);
  useEffect(() => {
    const t = setInterval(refresh, REFRESH_MS);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [refresh]);

  const value = useMemo(() => ({
    ready, flags, refresh,
    get: (key) => flags[key] || { enabled: false, variant: null, version: 0 },
  }), [ready, flags, refresh]);
  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

export function useFlags() {
  const ctx = useContext(FlagsContext);
  if (!ctx) throw new Error('useFlags must be used within FlagsProvider');
  return ctx;
}

/** { enabled, variant, ready } for one flag. Unknown flags are off. */
export function useFlag(key) {
  const { get, ready } = useFlags();
  const f = get(key);
  return { enabled: !!f.enabled, variant: f.variant || null, ready };
}

/** Render `children` only when the flag is on (optionally a specific variant). */
export function Flag({ name, variant = null, fallback = null, children }) {
  const f = useFlag(name);
  if (!f.enabled) return fallback;
  if (variant && f.variant !== variant) return fallback;
  return children;
}
